/**
 * NAVBAR COMPONENT
 * 
 * Simplified navigation with dropdown menus:
 * - My Items (logged in): Item type filters (Polishes, Plates, etc.) + All Items
 * - Explore (all): Discover, Marketplace, Events
 * - Help (all): FAQ, Guides, Feedback
 * - Profile Menu (logged in): My Collection/Creations/Destash/Profile/Wishlist, Settings, Sign Out
 * 
 * Features:
 * - Responsive design (desktop dropdowns, mobile accordion)
 * - Fixed profile picture sizing (no squishing)
 * - Click-outside to close dropdowns
 * - Glassmorphic dropdown styling
 * 
 * Note: "My Public Pages" removed - visibility status now shown
 * on each page via PageVisibilityBanner component
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { initializePushNotifications } from '../firebase/notifications';
import { AppIcons } from '../constants/icons';
import {
  ChevronDown,
  Menu,
  X,
  ClipboardList,
  Store,
  Tag,
  StickyNote,
  Info,
} from 'lucide-react';
import MessagesLink from './MessagesLink';
import NotificationBell from './NotificationBell';
import { getUserOwnedBrands } from '../services/brandService';

interface UserProfile {
  username: string;
  displayName: string;
  avatarUrl: string;
}

interface NavbarProps {
  onShowAuthModal: () => void;
}

type DropdownType = 'myStuff' | 'explore' | 'help' | 'profile' | null;

export default function Navbar({ onShowAuthModal }: NavbarProps) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<DropdownType>(null);
  const [mobileExpanded, setMobileExpanded] = useState<DropdownType>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [ownedBrand, setOwnedBrand] = useState<{ slug: string; name: string } | null>(null);
  
  const navRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for auth state changes and fetch profile
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoggedIn(!!user);
      setCurrentUser(user?.uid || null);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserProfile({
              username: data.profile?.username || data.username || '',
              displayName: data.profile?.displayName || data.displayName || '',
              avatarUrl: data.profile?.avatarUrl || data.avatarUrl || '',
            });
          }
          
          // Check if user owns any brands
          const brands = await getUserOwnedBrands(user.uid, user.email || undefined);
          if (brands.length > 0) {
            // Use the first owned brand (most users will only have one)
            setOwnedBrand({ slug: brands[0].slug, name: brands[0].name });
          } else {
            setOwnedBrand(null);
          }

          // Initialize push notifications (refresh FCM token if permission granted)
          initializePushNotifications(user.uid);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      } else {
        setUserProfile(null);
        setOwnedBrand(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setMobileMenuOpen(false);
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const toggleDropdown = (dropdown: DropdownType) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const toggleMobileSection = (section: DropdownType) => {
    setMobileExpanded(mobileExpanded === section ? null : section);
  };

  // Styling - Black text for visibility on rainbow background
  const textColor = 'text-gray-900';

  // Dropdown button component
  const DropdownButton = ({
    label,
    dropdown,
    icon: Icon
  }: {
    label: string;
    dropdown: DropdownType;
    icon?: React.ComponentType<{ className?: string }>;
  }) => (
    <button
      onClick={() => toggleDropdown(dropdown)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && activeDropdown === dropdown) {
          setActiveDropdown(null);
        } else if (e.key === 'ArrowDown' && activeDropdown !== dropdown) {
          e.preventDefault();
          toggleDropdown(dropdown);
        }
      }}
      aria-expanded={activeDropdown === dropdown}
      aria-haspopup="menu"
      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-default font-body ${
        activeDropdown === dropdown
          ? 'bg-slate-700 text-white'
          : `${textColor} hover:bg-slate-800/40`
      }`}
    >
      {Icon && <Icon className="icon-sm text-current" />}
      {label}
      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === dropdown ? 'rotate-180' : ''}`} />
    </button>
  );

  // Dropdown menu wrapper with glassmorphic styling
  const DropdownMenu = ({
    isOpen,
    children,
    align = 'left'
  }: {
    isOpen: boolean;
    children: React.ReactNode;
    align?: 'left' | 'right';
  }) => {
    if (!isOpen) return null;
    return (
      <div
        role="menu"
        aria-orientation="vertical"
        className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-2 w-64 glass-dropdown py-2 z-50`}
      >
        {children}
      </div>
    );
  };

  // Dropdown item component
  const DropdownItem = ({
    to,
    icon: Icon,
    children,
    onClick
  }: {
    to?: string;
    icon: React.ComponentType<{ className?: string }>;
    children: React.ReactNode;
    onClick?: () => void;
  }) => {
    const className = "flex items-center gap-3 px-4 py-2.5 text-glass-secondary hover:bg-slate-700 hover:text-white transition-default";
    const style = { fontFamily: 'Nunito, sans-serif' };

    if (to) {
      return (
        <Link
          to={to}
          role="menuitem"
          className={className}
          style={style}
          onClick={() => setActiveDropdown(null)}
        >
          <Icon className="icon-sm text-current" />
          {children}
        </Link>
      );
    }

    return (
      <button
        onClick={onClick}
        role="menuitem"
        className={`${className} w-full text-left`}
        style={style}
      >
        <Icon className="icon-sm text-current" />
        {children}
      </button>
    );
  };

  const DropdownDivider = () => <div className="border-t border-slate-600 my-2" />;

  return (
    <nav className="w-full z-50" ref={navRef}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Brand - hidden on homepage since it's redundant */}
          {!isHomePage ? (
            <Link
              to="/"
              className={`text-xl font-heading ${textColor} transition-default flex-shrink-0`}
            >
              Polish ALL the Things!
            </Link>
          ) : (
            <div className="flex-1" /> 
          )}
          
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`min-[915px]:hidden ${textColor} p-2`}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="icon-base text-current" /> : <Menu className="icon-base text-current" />}
          </button>
          
          {/* Desktop Navigation */}
          <div className="hidden min-[915px]:flex items-center gap-1">
            {/* My Items Dropdown - Logged in only */}
            {isLoggedIn && (
              <div className="relative">
                <DropdownButton label="My Items" dropdown="myStuff" />
                <DropdownMenu isOpen={activeDropdown === 'myStuff'}>
                  <DropdownItem to="/collection?type=acrylic" icon={AppIcons.Acrylic}>
                    Acrylics
                  </DropdownItem>
                  <DropdownItem to="/collection?type=cuticle_care" icon={AppIcons.CuticleCare}>
                    Cuticle Care
                  </DropdownItem>
                  <DropdownItem to="/collection?type=transfer" icon={AppIcons.Decal}>
                    Decals
                  </DropdownItem>
                  <DropdownItem to="/collection?type=embellishment" icon={AppIcons.Embellishment}>
                    Embellishments
                  </DropdownItem>
                  <DropdownItem to="/collection?type=plate" icon={AppIcons.Plate}>
                    Plates
                  </DropdownItem>
                  <DropdownItem to="/collection?type=polish" icon={AppIcons.Polish}>
                    Polishes
                  </DropdownItem>
                  <DropdownItem to="/collection?type=powder" icon={AppIcons.Powder}>
                    Powders
                  </DropdownItem>
                  <DropdownItem to="/collection?type=tool" icon={AppIcons.Tool}>
                    Tools
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem to="/collection" icon={AppIcons.ViewAll}>
                    All Items
                  </DropdownItem>
                </DropdownMenu>
              </div>
            )}

            {/* Explore Dropdown - Visible to all */}
            <div className="relative">
              <DropdownButton label="Explore" dropdown="explore" />
              <DropdownMenu isOpen={activeDropdown === 'explore'}>
                <DropdownItem to="/about" icon={Info}>
                  About
                </DropdownItem>
                <DropdownItem to="/updates" icon={StickyNote}>
                  Announcements
                </DropdownItem>
                <DropdownItem to="/brands" icon={AppIcons.Brands}>
                  Brands
                </DropdownItem>
                <DropdownItem to="/discover" icon={AppIcons.Discover}>
                  Discover
                </DropdownItem>
                <DropdownItem to="/events" icon={AppIcons.Events}>
                  Events Calendar
                </DropdownItem>
                <DropdownItem to="/marketplace" icon={AppIcons.Marketplace}>
                  Marketplace
                </DropdownItem>
                <DropdownItem to="/pricing" icon={Tag}>
                  Pricing
                </DropdownItem>
              </DropdownMenu>
            </div>

            {/* Help Dropdown - Visible to all */}
            <div className="relative">
              <DropdownButton label="Help" dropdown="help" />
              <DropdownMenu isOpen={activeDropdown === 'help'}>
                <DropdownItem to="/contact" icon={AppIcons.Contact}>
                  Contact Us
                </DropdownItem>
                <DropdownItem to="/help" icon={AppIcons.Help}>
                  FAQ
                </DropdownItem>
                {/*<DropdownItem to="/guides" icon={AppIcons.Guides}>*/}
                {/*  Guides*/}
                {/*</DropdownItem>*/}
                <DropdownItem to="/feedback" icon={AppIcons.Feedback}>
                  Feedback & Bugs
                </DropdownItem>
              </DropdownMenu>
            </div>

            {/* Right side: Notifications, Messages, Profile */}
            <div className="flex items-center gap-2 ml-4">
              {isLoggedIn ? (
                <>
                  {/* Notifications */}
                  <NotificationBell
                    userId={currentUser}
                    className={`${textColor} hover:bg-slate-800/40 rounded-lg px-3 py-2`}
                  />

                  {/* Messages */}
                  <MessagesLink
                    userId={currentUser}
                    className={`${textColor} hover:bg-slate-800/40 rounded-lg px-3 py-2`}
                  />

                  {/* Profile Dropdown */}
                  <div className="relative ml-2">
                    <button
                      onClick={() => toggleDropdown('profile')}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' && activeDropdown === 'profile') {
                          setActiveDropdown(null);
                        }
                      }}
                      aria-expanded={activeDropdown === 'profile'}
                      aria-haspopup="menu"
                      aria-label="Profile menu"
                      className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-default ${
                        activeDropdown === 'profile' ? 'bg-slate-700' : 'hover:bg-slate-800/40'
                      }`}
                    >
                      {userProfile?.avatarUrl ? (
                        <img
                          src={userProfile.avatarUrl}
                          alt={userProfile.displayName || userProfile.username}
                          className="w-8 h-8 rounded-full object-cover border-2 border-slate-600 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full rainbow-gradient flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {(userProfile?.displayName || userProfile?.username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <ChevronDown className={`w-4 h-4 text-gray-900 transition-transform ${activeDropdown === 'profile' ? 'rotate-180' : ''}`} />
                    </button>

                    <DropdownMenu isOpen={activeDropdown === 'profile'} align="right">
                      {userProfile?.username && (
                        <>
                          <DropdownItem to="/collection" icon={AppIcons.ViewAll}>
                            My Collection
                          </DropdownItem>
                          <DropdownItem to="/creations" icon={AppIcons.Creations}>
                            My Creations
                          </DropdownItem>
                          <DropdownItem to="/inspo" icon={AppIcons.Inspo}>
                            My Inspo
                          </DropdownItem>
                          <DropdownItem to={`/marketplace?seller=${userProfile.username}`} icon={AppIcons.Marketplace}>
                            My Destash
                          </DropdownItem>
                          <DropdownItem to={`/profile/${userProfile.username}`} icon={AppIcons.UserIcon}>
                            My Profile
                          </DropdownItem>
                          <DropdownItem to="/my-wants" icon={AppIcons.Wants}>
                            My Wishlist
                          </DropdownItem>
                          {ownedBrand && (
                            <>
                              <DropdownDivider />
                              <DropdownItem to={`/brands/${ownedBrand.slug}/manage`} icon={Store}>
                                My Brand
                              </DropdownItem>
                            </>
                          )}
                          <DropdownDivider />
                          <DropdownItem to="/wip" icon={ClipboardList}>
                            WIP Dashboard
                          </DropdownItem>
                          <DropdownItem to="/settings" icon={AppIcons.SettingsNav}>
                            Settings
                          </DropdownItem>
                        </>
                      )}
                      <DropdownDivider />
                      <DropdownItem icon={AppIcons.SignOut} onClick={handleSignOut}>
                        Sign Out
                      </DropdownItem>
                    </DropdownMenu>
                  </div>
                </>
              ) : (
                <button
                  onClick={onShowAuthModal}
                  className="btn-primary px-4 py-2 font-body"
                >
                  Sign Up / Login
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="min-[915px]:hidden mt-4 pb-4 border-t border-slate-600 pt-4 space-y-2 glass-dropdown p-4 -mx-2">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-glass hover:bg-slate-700 hover:text-glass-secondary rounded-lg transition-default font-body"
            >
              <AppIcons.Home className="icon-sm text-current" aria-label="Home" />
              Home
            </Link>

            {/* My Items - Logged in only */}
            {isLoggedIn && (
              <div>
                <button
                  onClick={() => toggleMobileSection('myStuff')}
                  aria-expanded={mobileExpanded === 'myStuff'}
                  className="w-full flex items-center justify-between px-4 py-2 text-glass hover:bg-slate-700 hover:text-glass-secondary rounded-lg transition-default font-body"
                >
                  <span className="flex items-center gap-2">
                    <AppIcons.ViewAll className="icon-sm text-current" aria-label="My Items" />
                    My Items
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === 'myStuff' ? 'rotate-180' : ''}`} />
                </button>
                {mobileExpanded === 'myStuff' && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-600 pl-4">
                    <Link to="/collection?type=acrylic" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Acrylic className="icon-xs text-current" aria-label="Acrylics" />
                      Acrylics
                    </Link>
                    <Link to="/collection?type=cuticle_care" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.CuticleCare className="icon-xs text-current" aria-label="Cuticle Care" />
                      Cuticle Care
                    </Link>
                    <Link to="/collection?type=transfer" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Decal className="icon-xs text-current" aria-label="Decals" />
                      Decals
                    </Link>
                    <Link to="/collection?type=embellishment" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Embellishment className="icon-xs text-current" aria-label="Embellishments" />
                      Embellishments
                    </Link>
                    <Link to="/collection?type=plate" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Plate className="icon-xs text-current" aria-label="Plates" />
                      Plates
                    </Link>
                    <Link to="/collection?type=polish" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Polish className="icon-xs text-current" aria-label="Polishes" />
                      Polishes
                    </Link>
                    <Link to="/collection?type=powder" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Powder className="icon-xs text-current" aria-label="Powders" />
                      Powders
                    </Link>
                    <Link to="/collection?type=tool" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.Tool className="icon-xs text-current" aria-label="Tools" />
                      Tools
                    </Link>
                    <div className="border-t border-slate-600 my-2"></div>
                    <Link to="/collection" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                      <AppIcons.ViewAll className="icon-xs text-current" aria-label="All Items" />
                      All Items
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Explore */}
            <div>
              <button
                onClick={() => toggleMobileSection('explore')}
                aria-expanded={mobileExpanded === 'explore'}
                className="w-full flex items-center justify-between px-4 py-2 text-glass hover:bg-slate-700 hover:text-glass-secondary rounded-lg transition-default font-body"
              >
                <span className="flex items-center gap-2">
                  <AppIcons.Explore className="icon-sm text-current" aria-label="Explore" />
                  Explore
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === 'explore' ? 'rotate-180' : ''}`} />
              </button>
              {mobileExpanded === 'explore' && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <Info className="icon-xs text-current" aria-label="About" />
                    About
                  </Link>
                  <Link to="/brands" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Brands className="icon-xs text-current" aria-label="Brands" />
                    Brands
                  </Link>
                  <Link to="/discover" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Discover className="icon-xs text-current" aria-label="Discover" />
                    Discover
                  </Link>
                  <Link to="/events" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Events className="icon-xs text-current" aria-label="Events Calendar" />
                    Events Calendar
                  </Link>
                  <Link to="/marketplace" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Marketplace className="icon-xs text-current" aria-label="Marketplace" />
                    Marketplace
                  </Link>
                  <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <Tag className="icon-xs text-current" aria-label="Pricing" />
                    Pricing
                  </Link>
                </div>
              )}
            </div>

            {/* Help */}
            <div>
              <button
                onClick={() => toggleMobileSection('help')}
                aria-expanded={mobileExpanded === 'help'}
                className="w-full flex items-center justify-between px-4 py-2 text-glass hover:bg-slate-700 hover:text-glass-secondary rounded-lg transition-default font-body"
              >
                <span className="flex items-center gap-2">
                  <AppIcons.Help className="icon-sm text-current" aria-label="Help" />
                  Help
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === 'help' ? 'rotate-180' : ''}`} />
              </button>
              {mobileExpanded === 'help' && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link to="/help" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Contact className="icon-xs text-current" aria-label="FAQ" />
                    Contact Us
                  </Link>
                  <Link to="/help" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Help className="icon-xs text-current" aria-label="FAQ" />
                    FAQ
                  </Link>
                  {/*<Link to="/guides" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">*/}
                  {/*  <AppIcons.Guides className="icon-xs text-current" aria-label="Guides" />*/}
                  {/*  Guides*/}
                  {/*</Link>*/}
                  <Link to="/feedback" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                    <AppIcons.Feedback className="icon-xs text-current" aria-label="Feedback & Bugs" />
                    Feedback & Bugs
                  </Link>
                </div>
              )}
            </div>

            {/* Logged in: Notifications, Messages, Profile section */}
            {isLoggedIn && (
              <>
                <div className="border-t border-slate-600 my-3"></div>

                {/* Notifications */}
                <div className="px-4 py-2">
                  <NotificationBell
                    userId={currentUser}
                    className="text-glass hover:text-glass-secondary"
                  />
                </div>

                {/* Messages */}
                <div className="px-4 py-2">
                  <MessagesLink
                    userId={currentUser}
                    className="text-glass hover:text-glass-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  />
                </div>

                <div className="border-t border-slate-600 my-3"></div>

                {/* Profile section */}
                {userProfile?.username && (
                  <>
                    <Link
                      to={`/profile/${userProfile.username}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-glass hover:bg-slate-700 hover:text-glass-secondary rounded-lg transition-default font-body"
                    >
                      {userProfile.avatarUrl ? (
                        <img
                          src={userProfile.avatarUrl}
                          alt={userProfile.displayName || userProfile.username}
                          className="w-10 h-10 rounded-full object-cover border-2 border-slate-600 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full rainbow-gradient flex items-center justify-center text-white font-bold flex-shrink-0">
                          {(userProfile.displayName || userProfile.username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-glass">{userProfile.displayName || userProfile.username}</div>
                        <div className="text-sm text-glass-muted">My Profile</div>
                      </div>
                    </Link>
                    
                    {/* My Pages - Edit Views */}
                    <div className="ml-4 space-y-1 border-l-2 border-slate-600 pl-4">
                      <Link to="/collection" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                        <AppIcons.ViewAll className="icon-xs text-current" aria-label="My Collection" />
                        My Collection
                      </Link>
                      <Link to="/creations" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                        <AppIcons.Creations className="icon-xs text-current" aria-label="My Creations" />
                        My Creations
                      </Link>
                      <Link to="/inspo" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                        <AppIcons.Inspo className="icon-xs text-current" aria-label="My Inspo" />
                        My Inspo
                      </Link>
                      <Link to={`/marketplace?seller=${userProfile.username}`} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                        <AppIcons.Marketplace className="icon-xs text-current" aria-label="My Destash" />
                        My Destash
                      </Link>
                      <Link to="/my-wants" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                        <AppIcons.Wants className="icon-xs text-current" aria-label="My Wishlist" />
                        My Wishlist
                      </Link>
                      {ownedBrand && (
                        <Link to={`/brands/${ownedBrand.slug}/manage`} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-purple-400 hover:text-purple-300 text-sm">
                          <Store className="icon-xs text-current" aria-label="My Brand" />
                          My Brand
                        </Link>
                      )}
                      <Link to="/wip" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 py-2 text-glass-secondary hover:text-glass text-sm">
                        <ClipboardList className="icon-xs text-current" aria-label="WIP Dashboard" />
                        WIP Dashboard
                      </Link>
                    </div>
                  </>
                )}

                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-glass hover:bg-slate-700 hover:text-glass-secondary rounded-lg transition-default font-body"
                >
                  <AppIcons.SettingsNav className="icon-sm text-current" aria-label="Settings" />
                  Settings
                </Link>

                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 text-left px-4 py-2 text-glass hover:bg-red-600/20 hover:text-red-400 rounded-lg transition-default font-body"
                >
                  <AppIcons.SignOut className="icon-sm text-current" aria-label="Sign Out" />
                  Sign Out
                </button>
              </>
            )}

            {/* Logged out: Sign Up button */}
            {!isLoggedIn && (
              <>
                <div className="border-t border-slate-600 my-3"></div>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onShowAuthModal();
                  }}
                  className="btn-primary w-full px-4 font-body"
                >
                  Sign Up / Login
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
